// What "the learner is enrolled in a programme" means for anything the learner
// sees: a list, the nav, a link (FOX-6893).
//
// `programs.enrolled.items[].cohorts` holds the learner's published, not-deleted
// cohorts. `programs.enrolled.total` counts every programme the learner has a
// cohort row in, including one whose only cohort is unpublished, so a filter on
// `total` shows a programme the learner cannot open (a link that 404s). Ask
// these predicates instead.
//
// This is the one module tools/lint/check-enrolled-total.mjs lets read
// `enrolled.total`. The reads frozen in its baseline are access and upsell
// gates; whether a learner in an unpublished cohort keeps access is a product
// decision (postmortem 261030_programs-empty-wrong-space, recommendation 2), so
// a gate that needs today's count gets a named predicate here, not a new read.

/** The slice of a programme the rule reads (an `enrolled.items` entry). */
export interface VisibleEnrolmentProgram {
  cohorts: ReadonlyArray<unknown>;
}

/** The slice of a space the rule reads (a slice of `SpaceMetadata`). */
export interface VisibleEnrolmentSpace {
  programs?: {
    enrolled: { items: ReadonlyArray<VisibleEnrolmentProgram> };
  } | null;
}

/** Does the learner have a cohort in this programme that they can see? */
export function hasVisibleCohort(program: VisibleEnrolmentProgram): boolean {
  return program.cohorts.length > 0;
}

/**
 * Is the learner enrolled in a programme they can see on this space?
 * `programs` is null on a space whose programs module is off.
 */
export function hasVisibleEnrolment(space: VisibleEnrolmentSpace): boolean {
  return space.programs?.enrolled.items.some(hasVisibleCohort) ?? false;
}
